/**
 * Ключи i18n и производные сопровождения (FE-260).
 *
 * Чистые функции без React: их можно проверить отдельно от разметки.
 *
 * **Backend не присылает готовых строк.** Он отдаёт коды и числа; собрать текст
 * — работа этого модуля и i18n (CLAUDE.md §6). Код без перевода на экране
 * показывать нельзя: сырой `ACCEPT_VIN` — утечка внутренностей.
 */

import type {
  ChecklistBlockName,
  Handover,
  HandoverChecklistBlock,
  HandoverChecklistItem,
} from './types';

/**
 * Ключ i18n пункта чек-листа.
 *
 * Пункты блока «что проверить лично» приходят с кодами движка заключений и уже
 * переведены на шаге 6 (`conclusion.checklist.*`) — второго перевода тем же
 * кодам не заводится: одно и то же в двух словарях разъезжается на первой же
 * правке. Приёмка — своя ветка словаря.
 */
export function checklistItemKey(block: ChecklistBlockName, code: string): string {
  return block === 'pre_purchase' ? `conclusion.checklist.${code}` : `handover.item.${code}`;
}

/** Ключ i18n пункта памятки: `before_signing` и `after_purchase` (§39.1). */
export function memoKey(group: string, code: string): string {
  return `handover.memo.${group}.${code}`;
}

/** Ключ i18n подписи блока чек-листа. */
export function blockKey(block: ChecklistBlockName): string {
  return `handover.block.${block}`;
}

/** Ключ i18n подписи исхода покупки. */
export function outcomeKey(value: string): string {
  return `handover.outcome.${value}`;
}

/** Блок чек-листа по имени. `null` — блока в ответе нет. */
export function blockOf(
  state: Handover | null | undefined,
  block: ChecklistBlockName,
): HandoverChecklistBlock | null {
  return state?.checklist.find((item) => item.block === block) ?? null;
}

/**
 * Подпись пункта, когда основанием служат слова автохауса.
 *
 * `label` — его формулировка, и она показывается **как его слова**, а не как
 * текст CarViQ.DE: своего названия обещания у продукта нет (§39.1).
 */
export function itemLabel(item: HandoverChecklistItem): string | null {
  const label = item.params['label'];
  return typeof label === 'string' && label.trim() !== '' ? label : null;
}

/**
 * Заказ завершён: исход отмечен, дальше только чтение (§39.3).
 *
 * Признак берётся у сервера (`status`), а не выводится из наличия отметки:
 * «ещё думаю» — тоже отметка, но заказ она не завершает.
 */
export function isCompleted(state: Handover | null | undefined): boolean {
  return state?.status === 'completed';
}

/**
 * Просьба о письменном подтверждении уже отправлена.
 *
 * `null` в `requested_at` означает «не просили», а не «нельзя просить»: кнопка
 * скрывается только после отправки, и повторную просьбу отклоняет backend.
 */
export function confirmationSent(state: Handover | null | undefined): boolean {
  return (
    state?.confirmation.requested_at !== null && state?.confirmation.requested_at !== undefined
  );
}

/**
 * Есть ли что просить подтвердить письменно (§39.1).
 *
 * Позиция существует только с цитатой (§42.3): пустой список означает, что
 * автохаус ничего сверх цены не обещал, — и кнопка не показывается вовсе.
 * Показать её и получить `409` было бы обещанием несуществующего действия.
 */
export function hasPromises(state: Handover | null | undefined): boolean {
  return (state?.terms?.promised.length ?? 0) > 0;
}
